import type {
  PaymentCreate,
  PaymentManagementPublic,
  PaymentManagementSummary,
  PaymentPublic,
} from '../type/payment';
import type { LifeStatus } from '../type/user';
import type { Payment, PaymentMemberRow, PaymentOverview } from '@/types';
import { getMonthYearFromDate } from '@/lib/payments';
import { toThreeDigits } from '@/lib/utils';
import { ManagementPolicyMapper } from './policy';

export const PaymentAPIResponseMapper = (payment: PaymentPublic): Payment => {
  const { month, year } = getMonthYearFromDate(payment.payment_date);

  return {
    id: String(payment.id),
    memberId: String(payment.user_id),
    policyId: payment.policy_id != null ? String(payment.policy_id) : undefined,
    policyNo: payment.policy_no ?? undefined,
    amount: payment.amount,
    paymentType: payment.payment_type,
    paymentDate: payment.payment_date,
    month,
    year,
    paymentNumber: payment.payment_number,
    duePeriod: payment.due_period ?? undefined,
    dueStart: payment.due_start ?? undefined,
    dueEnd: payment.due_end ?? undefined,
  };
};

export const PaymentAPIRequestMapper = (input: {
  policyId: string;
  amount: number;
  paymentType: Payment['paymentType'];
  paymentDate?: string;
  duePeriod: number;
  dueStart: string;
  dueEnd: string;
}): PaymentCreate => ({
  policy_id: Number(input.policyId),
  amount: input.amount,
  payment_type: input.paymentType,
  payment_date: input.paymentDate || new Date().toISOString().split('T')[0],
  due_period: input.duePeriod,
  due_start: input.dueStart,
  due_end: input.dueEnd,
});

export const PaymentManagementAPIResponseMapper = (item: PaymentManagementPublic): PaymentMemberRow => ({
  member: {
    id: String(item.user_id),
    memberNo: item.member_id != null ? toThreeDigits(item.member_id) : undefined,
    name: item.user_name ?? '',
    phone: item.user_phone ?? '',
    photo: item.user_profile || undefined,
    aadhaarNo: item.aadhaar_no ?? undefined,
    joinedDate: '',
    lifeStatus: (item.life_status?.toLowerCase() as LifeStatus) || 'alive',
  },
  policies: (item.policies ?? []).map((policy) => ManagementPolicyMapper(item.user_id, policy)),
  balance: {
    balanceDue: item.balance?.balance_due ?? 0,
    interestDue: item.balance?.interest_due ?? 0,
    hasHistory: item.balance?.has_history ?? false,
  },
});

export const PaymentOverviewMapper = (summary?: PaymentManagementSummary | null): PaymentOverview => ({
  totalMembers: summary?.total_members ?? 0,
  totalPolicies: summary?.total_policies ?? 0,
  membersWithLoan: summary?.members_with_loan ?? 0,
  policyPaidThisMonth: summary?.policy_paid_this_month ?? 0,
  policyUnpaidThisMonth: summary?.policy_unpaid_this_month ?? 0,
  policyPendingAmount: summary?.policy_pending_amount ?? 0,
  loanPaidThisMonth: summary?.loan_paid_this_month ?? 0,
  loanUnpaidThisMonth: summary?.loan_unpaid_this_month ?? 0,
  loanDueAmount: summary?.loan_due_amount ?? 0,
  policyCollected: summary?.policy_collected ?? 0,
  loanRepaid: summary?.loan_repaid ?? 0,
  loanOutstanding: summary?.loan_outstanding ?? 0,
  loansActive: summary?.loans_active ?? 0,
  loansGiven: summary?.loans_given ?? 0,
  memberBalanceDue: summary?.member_balance_due ?? 0,
  membersWithBalance: summary?.members_with_balance ?? 0,
  balanceInterestCharged: summary?.balance_interest_charged ?? 0,
});
